import { useRef, useState, useCallback, type ReactNode } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  /** Дистанция в px которую нужно потянуть для срабатывания (default: 80) */
  threshold?: number;
}

type PullState = 'idle' | 'pulling' | 'ready' | 'refreshing';

export const PullToRefresh = ({
  onRefresh,
  children,
  threshold = 80,
}: PullToRefreshProps) => {
  const [pullState, setPullState] = useState<PullState>('idle');
  const [pullDistance, setPullDistance] = useState(0);

  const startYRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isRefreshingRef = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollTop = containerRef.current?.scrollTop ?? 0;
    if (scrollTop === 0) {
      startYRef.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (startYRef.current === null || isRefreshingRef.current) return;

      const currentY = e.touches[0].clientY;
      const distance = currentY - startYRef.current;

      if (distance <= 0) {
        setPullDistance(0);
        setPullState('idle');
        return;
      }

      // Resistance effect — чем дальше тянешь тем тяжелее
      const resistedDistance = Math.min(distance * 0.5, threshold * 1.5);
      setPullDistance(resistedDistance);
      setPullState(resistedDistance >= threshold ? 'ready' : 'pulling');
    },
    [threshold]
  );

  const handleTouchEnd = useCallback(async () => {
    if (startYRef.current === null || isRefreshingRef.current) return;

    startYRef.current = null;

    if (pullState === 'ready') {
      isRefreshingRef.current = true;
      setPullState('refreshing');
      setPullDistance(threshold * 0.75);

      try {
        await onRefresh();
      } finally {
        isRefreshingRef.current = false;
        setPullState('idle');
        setPullDistance(0);
      }
    } else {
      setPullState('idle');
      setPullDistance(0);
    }
  }, [pullState, onRefresh, threshold]);

  const isVisible = pullDistance > 0 || pullState === 'refreshing';
  const progress = Math.min(pullDistance / threshold, 1);
  const rotation = pullState === 'refreshing' ? undefined : `${progress * 360}deg`;

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        // flex-1 + overflow-y-auto чтобы занимать всю доступную высоту
        // и реагировать на touch в любой точке страницы
        flex: 1,
        overflowY: 'auto',
        position: 'relative',
        // Минимальная высота = весь экран минус header (~56px)
        minHeight: 0,
      }}
    >
      {/* Индикатор */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: isVisible ? `${pullDistance}px` : '0px',
          overflow: 'hidden',
          transition: pullState === 'idle' ? 'height 0.3s ease' : undefined,
          pointerEvents: 'none',
        }}
      >
        {/* Спиннер — цвет через CSS переменные темы */}
        <div
          className="ptr-spinner"
          style={{
            transform: pullState === 'refreshing' ? undefined : `rotate(${rotation})`,
            animation: pullState === 'refreshing' ? 'ptr-spin 0.7s linear infinite' : undefined,
            opacity: Math.max(progress, pullState === 'refreshing' ? 1 : 0),
            transition: pullState !== 'refreshing' ? 'opacity 0.1s' : undefined,
          }}
        />
      </div>

      {children}

      <style>{`
        .ptr-spinner {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 3px solid var(--purple-light);
          border-top-color: var(--primary-purple);
          flex-shrink: 0;
        }
        @keyframes ptr-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};